import { formatDateBR } from '../../utils/dateUtils';
import { useState, useMemo } from 'react';
import { CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useQuery } from '@tanstack/react-query';
import { colaboradoresService, type AgendamentoColaborador } from '../../services/colaboradores.service';
import { Loader2 } from 'lucide-react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { documentosService } from '../../services/documentos.service';
import { useToast } from '../../components/Toast';
import { getCustomerApiBaseURL } from '../../utils/apiConfig';

interface ExamesAgendamentosTabProps {
  colaboradorId: number;
}

const statusColors: Record<string, string> = {
  agendado: 'bg-blue-100 text-blue-800',
  confirmado: 'bg-blue-100 text-blue-800',
  compareceu: 'bg-sky-100 text-sky-800',
  presente: 'bg-sky-100 text-sky-800',
  'aguardando resultado': 'bg-amber-100 text-amber-800',
  aguardando_resultado: 'bg-amber-100 text-amber-800',
  'em auditoria': 'bg-blue-100 text-blue-800',
  em_auditoria: 'bg-blue-100 text-blue-800',
  realizado: 'bg-emerald-100 text-emerald-800',
  concluido: 'bg-emerald-100 text-emerald-800',
  finalizado: 'bg-emerald-100 text-emerald-800',
  parcial: 'bg-indigo-100 text-indigo-800',
  pago: 'bg-blue-100 text-blue-800',
  pendente: 'bg-yellow-100 text-yellow-800',
  'não compareceu': 'bg-amber-100 text-amber-800',
  nao_compareceu: 'bg-amber-100 text-amber-800',
  faltou: 'bg-amber-100 text-amber-800',
  faltou_no_show: 'bg-amber-100 text-amber-800',
  'no-show': 'bg-amber-100 text-amber-800',
  no_show: 'bg-amber-100 text-amber-800',
  cancelado: 'bg-red-100 text-red-800',
};

function isNoShowStatus(statusStr: string | undefined | null): boolean {
  if (!statusStr) return false;
  const s = statusStr.toLowerCase().trim();
  return ['faltou', 'não compareceu', 'nao_compareceu', 'faltou_no_show', 'no-show', 'no_show'].includes(s);
}

function calcularStatusOperacionalGrupo(agendamentos: AgendamentoColaborador[]): string {
  if (!agendamentos || agendamentos.length === 0) return 'pendente';
  const descs = agendamentos.map((a) => (a.status || 'pendente').toLowerCase().trim());
  if (descs.every((d) => d === 'cancelado')) return 'cancelado';

  const todosFaltaram = descs.every((d) => isNoShowStatus(d));
  if (todosFaltaram) return 'não compareceu';

  const todosRealizados = descs.every((d) => ['realizado', 'concluido', 'finalizado'].includes(d));
  if (todosRealizados) return 'realizado';

  const algumRealizado = descs.some((d) => ['realizado', 'concluido', 'finalizado'].includes(d));
  if (algumRealizado) return 'parcial';

  const algumAgendado = descs.some((d) => ['agendado', 'confirmado'].includes(d));
  if (algumAgendado) return 'agendado';

  const algumFaltou = descs.some((d) => isNoShowStatus(d));
  if (algumFaltou) return 'não compareceu';

  return 'pendente';
}

// Interface auxiliar para Subgrupo (Data + Clínica)
interface SubGrupoAgendamento {
  key: string;
  dataAgendamento: string;
  clinica: AgendamentoColaborador['clinica'];
  agendamentos: AgendamentoColaborador[];
}

// Interface para o Grupo Principal (Pedido)
interface GrupoAgendamento {
  key: string;
  pedidoId: number | string;
  statusOperacional: string;
  pedidoStatus?: string;
  pedidoTipoPagamento?: string; // NOVO: Tipo de pagamento
  dataPedido?: string; // Data do pedido (ou do primeiro agendamento)
  tipoExame: string;
  avulso: boolean;
  subGrupos: SubGrupoAgendamento[];
  todosAgendamentos: AgendamentoColaborador[];
}

export default function ExamesAgendamentosTab({ colaboradorId }: ExamesAgendamentosTabProps) {
  const { hasFeature } = useFeatureAccess();
  const { error: toastError } = useToast();
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  const { data: agendamentos = [], isLoading } = useQuery({
    queryKey: ['colaborador-agendamentos', colaboradorId],
    queryFn: () => colaboradoresService.getAgendamentos(colaboradorId),
  });

  const formatDate = (dateString: string) => {
    try {
      return formatDateBR(dateString);
    } catch {
      return dateString;
    }
  };

  const agendados = agendamentos.filter(a => ['agendado', 'confirmado'].includes(a.status?.toLowerCase()));
  const realizados = agendamentos.filter(a => ['realizado', 'concluido', 'finalizado'].includes(a.status?.toLowerCase()));
  const faltas = agendamentos.filter(a => isNoShowStatus(a.status));
  const pendentes = agendamentos.filter(a => a.status?.toLowerCase() === 'pendente');

  // Lógica de Agrupamento Hierárquico
  const grupos = useMemo(() => {
    const gruposMap = new Map<string, GrupoAgendamento>();

    // 1. Agrupar por Pedido
    agendamentos.forEach(ag => {
      const key = ag.pedido?.id ? `pedido_${ag.pedido.id}` : `data_${ag.data_agendamento}`;

      if (!gruposMap.has(key)) {
        gruposMap.set(key, {
          key,
          pedidoId: ag.pedido?.id || ag.id || 'Sem Pedido',
          statusOperacional: 'pendente',
          pedidoStatus: ag.pedido?.status,
          pedidoTipoPagamento: ag.pedido?.tipo_pagamento, // NOVO: Mapeia do backend
          dataPedido: ag.data_agendamento, // Usa data do primeiro item como ref
          tipoExame: ag.tipo,
          avulso: !ag.pedido?.id,
          subGrupos: [],
          todosAgendamentos: []
        });
      }
      gruposMap.get(key)!.todosAgendamentos.push(ag);
    });

    // 2. Sub-agrupar por Data+Clínica dentro de cada Pedido
    return Array.from(gruposMap.values()).map(grupo => {
      const subMap = new Map<string, SubGrupoAgendamento>();

      grupo.todosAgendamentos.forEach(ag => {
        const subKey = `${ag.data_agendamento}_${ag.clinica.id}`;
        if (!subMap.has(subKey)) {
          subMap.set(subKey, {
            key: subKey,
            dataAgendamento: ag.data_agendamento,
            clinica: ag.clinica,
            agendamentos: []
          });
        }
        subMap.get(subKey)!.agendamentos.push(ag);
      });

      grupo.statusOperacional = calcularStatusOperacionalGrupo(grupo.todosAgendamentos);
      grupo.subGrupos = Array.from(subMap.values());
      return grupo;
    }).sort((a, b) => {
      // Ordenar grupos por data mais recente
      const dateA = a.dataPedido ? new Date(a.dataPedido).getTime() : 0;
      const dateB = b.dataPedido ? new Date(b.dataPedido).getTime() : 0;
      return dateB - dateA;
    });
  }, [agendamentos]);

  const handleDownloadDocument = async (
    tipo: 'guia' | 'aso' | 'ficha-medica',
    pedidoId: number | string | undefined,
    colaboradorId: number,
    fallbackAgendamentoId?: number,
  ) => {
    let refId: number | null = null;
    if (typeof pedidoId === 'number' && !isNaN(pedidoId) && pedidoId > 0) {
      refId = pedidoId;
    } else if (typeof pedidoId === 'string' && /^\d+$/.test(pedidoId.trim())) {
      refId = parseInt(pedidoId.trim(), 10);
    } else if (fallbackAgendamentoId && fallbackAgendamentoId > 0) {
      refId = fallbackAgendamentoId;
    }

    if (!refId) {
      toastError('Identificador do pedido ou agendamento não encontrado para emissão do documento.');
      return;
    }

    const key = `${tipo}_${refId}`;
    if (downloading[key]) return;

    try {
      setDownloading(prev => ({ ...prev, [key]: true }));

      const codigo = btoa(`${refId}_${colaboradorId}`);

      const documentosMap = {
        'guia': { fn: documentosService.downloadGuia, name: 'Guia' },
        'aso': { fn: documentosService.downloadASO, name: 'ASO' },
        'ficha-medica': { fn: documentosService.downloadFichaMedica, name: 'Ficha' }
      };

      const isMobile = typeof navigator !== 'undefined' &&
        /android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent);

      if (isMobile) {
        const directUrl = `${getCustomerApiBaseURL()}/documentos/${tipo}/${codigo}?view=true`;
        const opened = window.open(directUrl, '_blank');
        if (!opened) {
          window.location.href = directUrl;
        }
        return;
      }

      const docConfig = documentosMap[tipo];
      const blob = await docConfig.fn.call(documentosService, codigo);
      const filename = `${docConfig.name}-${pedidoId}-${colaboradorId}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error('Erro ao baixar documento:', err);
      toastError('Erro ao gerar documento. Tente novamente.');
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="py-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--h1Color)' }}>
          Exames e Agendamentos
        </h3>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <div className="card text-center">
            <div className="text-2xl font-bold" style={{ color: 'var(--h1Color)' }}>
              {agendamentos.length}
            </div>
            <div className="text-gray-600 text-sm">Total</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-blue-600">{agendados.length}</div>
            <div className="text-gray-600 text-sm">Agendados</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-green-600">{realizados.length}</div>
            <div className="text-gray-600 text-sm">Realizados</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-amber-600">{faltas.length}</div>
            <div className="text-gray-600 text-sm">Faltas (No-Show)</div>
          </div>
          <div className="card text-center">
            <div className="text-2xl font-bold text-yellow-600">
              {pendentes.length}
            </div>
            <div className="text-gray-600 text-sm">Pendentes</div>
          </div>
        </div>
      </div>

      {grupos.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          Nenhum agendamento encontrado
        </div>
      ) : (
        <div className="space-y-6">
          {grupos.map((grupo) => {
            const isPedido = !grupo.avulso;
            const statusOp = grupo.statusOperacional;
            const statusPedido = grupo.pedidoStatus || 'pendente';
            const tipoPagamento = grupo.pedidoTipoPagamento;

            // Lógica de Permissão dos Botões (Grupo/Pedido)
            const isFaturamento = tipoPagamento === 'faturamento';
            const isPago = statusPedido === 'pago';
            const isStatusPagamentoValido = isPago || statusPedido === 'concluido' || isFaturamento;

            const hasFeaturePermission = grupo.avulso || hasFeature('clinical_exams');
            const anyValidItem = grupo.todosAgendamentos.some(a => ['agendado', 'realizado', 'concluido', 'finalizado', 'confirmado'].includes(a.status));
            const showButtons = (isPedido || anyValidItem) && hasFeaturePermission && isStatusPagamentoValido;

            return (
              <div key={grupo.key} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Header do Pedido */}
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      {isPedido ? (
                        <span className="text-sm font-bold text-gray-900 bg-gray-200 px-2 py-0.5 rounded">
                          Pedido #{grupo.pedidoId}
                        </span>
                      ) : (
                        <span className="text-sm font-bold text-gray-700 bg-gray-200 px-2 py-0.5 rounded">
                          Agendamento Avulso #{grupo.pedidoId}
                        </span>
                      )}
                      {grupo.dataPedido && (
                        <span className="text-sm font-medium text-gray-600 flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          {formatDate(grupo.dataPedido)}
                        </span>
                      )}
                      {tipoPagamento && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                          {tipoPagamento === 'faturamento' ? 'Faturamento Corporativo' : tipoPagamento.toUpperCase()}
                        </span>
                      )}
                      {isPago && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">
                          Pago
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700 font-semibold">{grupo.tipoExame}</div>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Status Operacional do Atendimento */}
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[statusOp as keyof typeof statusColors] || statusColors.pendente
                      }`}>
                      {statusOp === 'parcial'
                        ? 'Parcialmente Realizado'
                        : isNoShowStatus(statusOp)
                        ? 'Não Compareceu (No-Show)'
                        : statusOp.charAt(0).toUpperCase() + statusOp.slice(1)}
                    </span>
                  </div>
                </div>

                {/* Corpo do Cartão - Subgrupos */}
                <div className="p-6">
                  {grupo.subGrupos.map((subGrupo, idx) => (
                    <div key={subGrupo.key} className={`${idx > 0 ? 'mt-6 pt-6 border-t border-gray-100' : ''}`}>

                      {/* Dados de Data/Local (Exibidos uma vez por subgrupo) */}
                      <div className="flex items-start gap-4 mb-3 text-sm text-gray-600 bg-gray-50/50 p-2 rounded">
                        <div className="flex items-center gap-2">
                          <CalendarIcon className="w-4 h-4 text-green-600" />
                          <span className="font-medium text-gray-900">{formatDate(subGrupo.dataAgendamento)}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <MapPinIcon className="w-4 h-4 text-green-600 mt-0.5" />
                          <div>
                            <span className="font-medium text-gray-900 block">{subGrupo.clinica.nome}</span>
                            <span className="text-xs text-gray-500">{subGrupo.clinica.endereco}</span>
                          </div>
                        </div>
                      </div>

                      {/* Lista de Exames do Subgrupo */}
                      <div className="space-y-2 pl-2">
                        {subGrupo.agendamentos.map(ag => (
                          <div key={ag.id} className="flex justify-between items-center py-1 border-b border-gray-50 last:border-0 hover:bg-gray-50 p-1 rounded">
                            <div className="flex items-center gap-2">
                              {/* Bolinha de status */}
                              <span className={`w-2 h-2 rounded-full ${
                                ag.status === 'pendente' ? 'bg-yellow-400' :
                                ['realizado', 'concluido', 'finalizado'].includes(ag.status) ? 'bg-emerald-500' :
                                ['compareceu', 'presente'].includes(ag.status) ? 'bg-sky-500' :
                                ag.status?.includes('resultado') ? 'bg-amber-400' :
                                isNoShowStatus(ag.status) ? 'bg-amber-500' :
                                ag.status === 'cancelado' ? 'bg-red-500' :
                                'bg-blue-400'
                              }`} />
                              <span className="text-gray-800 font-medium">
                                {ag.exame_clinico_nome || ag.exame}
                              </span>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded border ${statusColors[ag.status as keyof typeof statusColors] || 'bg-gray-50 border-gray-200'
                              } bg-opacity-20 border-opacity-20`}>
                              {isNoShowStatus(ag.status) ? 'Não Compareceu' : ag.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Botões de Ação (Rodapé do Pedido) */}
                  {showButtons && (
                    <div className="flex flex-wrap gap-2 justify-end mt-6 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handleDownloadDocument('guia', grupo.pedidoId, colaboradorId, grupo.todosAgendamentos[0]?.id)}
                        disabled={downloading[`guia_${grupo.pedidoId}`] || downloading[`guia_${grupo.todosAgendamentos[0]?.id}`]}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {downloading[`guia_${grupo.pedidoId}`] || downloading[`guia_${grupo.todosAgendamentos[0]?.id}`] ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        )}
                        {downloading[`guia_${grupo.pedidoId}`] || downloading[`guia_${grupo.todosAgendamentos[0]?.id}`] ? 'Gerando...' : 'Imprimir Guia'}
                      </button>
                      <button
                        onClick={() => handleDownloadDocument('aso', grupo.pedidoId, colaboradorId, grupo.todosAgendamentos[0]?.id)}
                        disabled={downloading[`aso_${grupo.pedidoId}`] || downloading[`aso_${grupo.todosAgendamentos[0]?.id}`]}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {downloading[`aso_${grupo.pedidoId}`] || downloading[`aso_${grupo.todosAgendamentos[0]?.id}`] ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        )}
                        {downloading[`aso_${grupo.pedidoId}`] || downloading[`aso_${grupo.todosAgendamentos[0]?.id}`] ? 'Gerando...' : 'Imprimir ASO'}
                      </button>
                      <button
                        onClick={() => handleDownloadDocument('ficha-medica', grupo.pedidoId, colaboradorId, grupo.todosAgendamentos[0]?.id)}
                        disabled={downloading[`ficha-medica_${grupo.pedidoId}`] || downloading[`ficha-medica_${grupo.todosAgendamentos[0]?.id}`]}
                        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {downloading[`ficha-medica_${grupo.pedidoId}`] ? (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                        )}
                        {downloading[`ficha-medica_${grupo.pedidoId}`] ? 'Gerando...' : 'Imprimir Ficha Médica'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
